import {
  ChartLineUp,
  Folder,
  ListChecks,
  ShieldCheck,
  Sparkle,
} from "@phosphor-icons/react";
import { Box, Button, Flex, Heading, Text, TextArea } from "@radix-ui/themes";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { useCreateAskPunttChat } from "../../utils/queries/askPuntt";
import { useRoiDashboard } from "../../utils/queries/roiDashboard";
import { askPunttChatRoute } from "../ask-puntt/chatRoute";

/**
 * Vitalora demo homepage (S1 scene from the build brief).
 *
 * This route is reached only by users on the demo tenant — the index
 * route at `routes/index/route.ts` branches non-demo users to ask-puntt
 * directly, preserving the existing behaviour for real customers.
 *
 * Layout matches the build brief:
 *   - Stat strip ("2,847 reviews this quarter | 78% cycle time
 *     reduction | $1.2M estimated savings")
 *   - Big Ask Puntt input front-and-centre
 *   - Live activity feed on the right (5 most recent events)
 *   - Quick-access tiles row underneath
 *
 * The activity feed strings mirror the seeded `IntegrationEvent` rows in
 * `packages/commons-node/src/seed/vitalora/integrationEvents.ts` so the
 * canvas reads correctly even before any backend roundtrip happens.
 */
export default function VitaloraHome() {
  const navigate = useNavigate();
  const createChat = useCreateAskPunttChat();
  const [inputMessage, setInputMessage] = useState("");

  const roi = useRoiDashboard();
  const stats = roi.data?.summaryStats;

  const handleSubmit = async () => {
    const msg = inputMessage.trim();
    if (!msg) return;
    try {
      const result = await createChat.mutateAsync({
        message: msg,
        workflowType: "general",
      });
      setInputMessage("");
      navigate({
        to: askPunttChatRoute.to,
        params: { chatId: result.chat._id },
      });
    } catch (err) {
      console.error("[vitalora-home] failed to create chat", err);
    }
  };

  return (
    <div
      className="ask-puntt-mesh-bg ask-puntt-noise relative flex h-full flex-col overflow-y-auto"
      data-testid="vitalora-home"
    >
      <StatStrip stats={stats} />

      <div className="mx-auto grid w-full max-w-screen-xl flex-1 grid-cols-1 gap-6 p-6 md:grid-cols-[2fr,1fr]">
        <Flex direction="column" gap="6" justify="center">
          <Box className="text-center">
            <Heading size="8" weight="bold" mb="2">
              Ask{" "}
              <span
                style={{
                  backgroundImage:
                    "linear-gradient(135deg, rgb(var(--puntt-blue-9)), rgb(var(--puntt-blue-11)))",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                }}
              >
                Puntt
              </span>
            </Heading>
            <Text size="3" color="gray">
              Run a review, check a claim, or diagnose a bottleneck — same door
              for humans and agents.
            </Text>
          </Box>

          <Flex
            direction="column"
            className="rounded-2xl border border-puntt-neutral-gray-6 bg-base-white p-3 shadow-lg focus-within:border-puntt-accent-7"
          >
            <TextArea
              data-testid="vitalora-home-input"
              size="3"
              variant="surface"
              placeholder="Ask Puntt anything..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              style={{ minHeight: 96, border: "none", boxShadow: "none" }}
            />
            <Flex justify="end" mt="2">
              <Button
                data-testid="vitalora-home-ask-button"
                size="2"
                onClick={() => handleSubmit()}
                disabled={!inputMessage.trim() || createChat.isPending}
              >
                Ask
              </Button>
            </Flex>
          </Flex>

          <QuickAccessTiles />
        </Flex>

        <ActivityFeed />
      </div>
    </div>
  );
}

function StatStrip({
  stats,
}: {
  stats:
    | {
        reviewsThisQuarter?: number;
        cycleTimeReduction?: number;
        estimatedSavings?: number;
        revisionsBefore?: number;
        revisionsAfter?: number;
        avgTurnaroundHrs?: number;
      }
    | undefined;
}) {
  // Fallbacks here are the same numbers the demo deck quotes — keeps the
  // bar populated immediately on first paint, before the ROI query
  // resolves, so there is no flash of an empty strip.
  const reviews = stats?.reviewsThisQuarter ?? 2847;
  const cycle = Math.round((stats?.cycleTimeReduction ?? 0.78) * 100);
  const savings = stats?.estimatedSavings ?? 1_200_000;

  return (
    <div className="border-b border-puntt-neutral-gray-6 bg-puntt-neutral-gray-1">
      <Flex
        align="center"
        justify="between"
        gap="6"
        wrap="wrap"
        className="mx-auto w-full max-w-screen-xl px-6 py-3"
      >
        <Stat label="Reviews this quarter" value={reviews.toLocaleString()} />
        <Stat label="Cycle time reduction" value={`${cycle}%`} />
        <Stat
          label="Estimated annual savings"
          value={`$${(savings / 1_000_000).toFixed(1)}M`}
        />
      </Flex>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Flex direction="column" gap="0">
      <Text size="5" weight="bold">
        {value}
      </Text>
      <Text size="1" color="gray">
        {label}
      </Text>
    </Flex>
  );
}

// IMPORTANT: keep these as literal path strings, not `someRoute.to`
// references. TanStack Route objects build their `.to` value at
// `addChildren` time, but `vitalora-home/view.tsx` is imported by
// `routes/index.ts` ahead of `tickets/route` and `roi/route`, so
// reading `ticketsRoute.to`/`roiRoute.to` here would TDZ-crash the
// entire bundle ("Cannot access 'Ir' before initialization"). The
// literal strings are stable (TanStack accepts them in `<Link to=…>`).
const QUICK_TILES = [
  {
    label: "Start a Review",
    icon: ListChecks,
    to: "/tickets",
    description: "Run brand, regulatory, copy + legal in parallel.",
  },
  {
    label: "Check a Claim",
    icon: ShieldCheck,
    to: "/tickets",
    description: "Verify against approved claims + market regs.",
  },
  {
    label: "View ROI Dashboard",
    icon: ChartLineUp,
    to: "/roi",
    description: "Cycle time, accuracy, savings — quarter to date.",
  },
  {
    label: "Open Workflows",
    icon: Folder,
    to: "/tickets",
    description: "Brief Review, Asset Review, Claims, Routing.",
  },
] as const;

function QuickAccessTiles() {
  return (
    <Flex gap="3" wrap="wrap">
      {QUICK_TILES.map((t) => (
        <Link
          key={t.label}
          to={t.to}
          className="flex flex-1 basis-[220px] flex-col gap-2 rounded-xl border border-puntt-neutral-gray-6 bg-base-white p-4 transition hover:border-puntt-accent-7 hover:shadow-md"
        >
          <Flex align="center" gap="2">
            <t.icon
              size={22}
              weight="duotone"
              className="text-puntt-accent-9"
            />
            <Text weight="bold">{t.label}</Text>
          </Flex>
          <Text size="1" color="gray">
            {t.description}
          </Text>
        </Link>
      ))}
    </Flex>
  );
}

interface ActivityRow {
  time: string;
  title: string;
  detail: string;
  icon: typeof Sparkle;
}

const ACTIVITY_ROWS: ActivityRow[] = [
  {
    time: "09:00",
    title: "Reminder sent to Priya",
    detail:
      "Wrike #4421 (Final Artwork Sign-off) — 6 days unactioned. Slack DM + email.",
    icon: Sparkle,
  },
  {
    time: "08:45",
    title: "Tom Walsh load assessed",
    detail:
      "11 overdue tasks · protein claim flagged for reassignment to Priya.",
    icon: ShieldCheck,
  },
  {
    time: "08:30",
    title: "Luminos Social Brief flagged",
    detail:
      "Wrike comment posted: missing audience + dimensions, deadline auto-adjusted.",
    icon: ListChecks,
  },
  {
    time: "Yesterday 17:00",
    title: "Luminos SPF30 routed",
    detail: "MEDIUM RISK · routed to James Rivera after AI review.",
    icon: ChartLineUp,
  },
  {
    time: "Yesterday 16:50",
    title: "VitaCore 800g auto-approved",
    detail: "All 5 checks passed · stakeholders notified in Slack.",
    icon: Sparkle,
  },
];

function ActivityFeed() {
  return (
    <div
      className="rounded-2xl border border-puntt-neutral-gray-6 bg-base-white p-4 shadow-sm"
      data-testid="vitalora-activity-feed"
    >
      <Flex align="center" justify="between" mb="3">
        <Text weight="bold">Agent activity · last 24h</Text>
        <Link
          to="/agents/activity"
          className="text-sm text-puntt-accent-11 hover:underline"
        >
          View all
        </Link>
      </Flex>
      <Flex direction="column" gap="3">
        {ACTIVITY_ROWS.map((row) => (
          <Flex key={`${row.time}-${row.title}`} gap="3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-puntt-accent-3 text-puntt-accent-11">
              <row.icon size={16} weight="duotone" />
            </div>
            <Flex direction="column" className="flex-1">
              <Flex align="baseline" justify="between" gap="2">
                <Text size="2" weight="bold">
                  {row.title}
                </Text>
                <Text size="1" color="gray">
                  {row.time}
                </Text>
              </Flex>
              <Text size="1" color="gray">
                {row.detail}
              </Text>
            </Flex>
          </Flex>
        ))}
      </Flex>
    </div>
  );
}
